/**
 * @file App-owned parent detail adapter composition.
 */

import { assetDetailAdapter } from '../../domains/assets/assetDetailAdapter.js';
import { patientDetailAdapter } from '../../domains/patients/patientDetailAdapter.js';
import { projectDetailAdapter } from '../../domains/projects/projectDetailAdapter.js';
import { taskDetailAdapter } from '../../domains/tasks/taskDetailAdapter.js';
import { labDetailAdapter } from '../../domains/labs/labsDetails.js';
import { maintenanceDetailAdapter } from '../../domains/maintenance/maintenanceDetails.js';

const DETAIL_ADAPTERS = new Map([
    [assetDetailAdapter.itemType, assetDetailAdapter],
    [patientDetailAdapter.itemType, patientDetailAdapter],
    [projectDetailAdapter.itemType, projectDetailAdapter],
    [taskDetailAdapter.itemType, taskDetailAdapter],
    [labDetailAdapter.itemType, labDetailAdapter],
    [maintenanceDetailAdapter.itemType, maintenanceDetailAdapter],
]);

/**
 * Resolves the domain detail adapter for an item type.
 * @param {string | null} itemType Singular item type.
 * @returns {object | null} Matching detail adapter, if registered.
 */
export function getParentDetailAdapter(itemType) {
    if (!itemType) {return null;}
    return DETAIL_ADAPTERS.get(itemType) || null;
}

/**
 * Resolves status options for a registered detail adapter.
 * @param {object | null} adapter Detail adapter.
 * @param {object} appData App data cache.
 * @returns {Array<object>} Configured status options.
 */
export function getParentDetailStatusOptions(adapter, appData) {
    const configKey = adapter?.statusConfigKey;
    return configKey && Array.isArray(appData?.config?.[configKey]) ?
        appData.config[configKey] :
        [];
}

/**
 * Resolves type options for a registered detail adapter.
 * @param {object | null} adapter Detail adapter.
 * @param {object} appData App data cache.
 * @returns {Array<object>} Configured type options.
 */
export function getParentDetailTypeOptions(adapter, appData) {
    const configKey = adapter?.typeConfigKey;
    return configKey && Array.isArray(appData?.config?.[configKey]) ?
        appData.config[configKey] :
        [];
}

/**
 * Renders the post-form sections declared by a detail adapter.
 * @param {object} adapter Detail adapter.
 * @param {object} item Active detail item.
 * @param {object<string, Function>} sharedRenderers Shared section renderers.
 * @returns {string} Joined section HTML.
 */
export function renderParentDetailSections(adapter, item, sharedRenderers) {
    return (adapter?.sections || [])
        .map((sectionId) => {
            const customHtml = adapter.renderSection?.(sectionId, item);
            if (typeof customHtml === 'string') {return customHtml;}

            const renderer = sharedRenderers?.[sectionId];
            return typeof renderer === 'function' ? renderer(item) : '';
        })
        .join('');
}
